// @flow

import * as React from "react";
import copy from "copy-to-clipboard";

import { type DateLike } from "app/lib/date";
import LegacyDispatcher from "app/lib/legacyDispatcher";
import parseEmoji from "app/lib/parseEmoji";

import Duration from "app/components/shared/Duration";
import LegacySpinner from "app/components/shared/LegacySpinner";

import JobLogGroupStore from "app/stores/JobLogGroupStore";
import JobLogLineStore from "app/stores/JobLogLineStore";

import JobLogOutputTimestamp from "./Timestamp";
import CopyPermalink from "./CopyPermalink";
import classNames from "classnames";

const getStateFromStores = (id) => ({
  highlighted: JobLogLineStore.isHighlighted(id),
  expanded: JobLogGroupStore.isExpanded(id),
});

type Props = {
  id: string,
  name: string,
  number: number,
  startedAt: ?DateLike,
  finishedAt: ?DateLike,
  finished: boolean,
  durationPercentage: ?number,
  bootstrap?: boolean,
  timestampsOn: ?boolean,
  timestampsInUTC: ?boolean,
  onTimestampClick: ?(SyntheticMouseEvent<HTMLElement>) => void,
};

type State = {
  highlighted: boolean,
  expanded: boolean,
  showCopyPermalink: boolean,
};

export default class JobLogOutputHeader extends React.PureComponent<
  Props,
  State,
> {
  wrapperNode: ?HTMLTableRowElement;

  constructor(props: Props) {
    super(props);

    this.state = {
      ...getStateFromStores(props.id),
      showCopyPermalink: false,
    };
  }

  // Notify the stores that a highlighted line has been rendered. This gives the
  // stores an opportunity to scroll to this line if they need to.
  dispatchRenderedHighlightAction() {
    if (this.state.highlighted) {
      return LegacyDispatcher.emit("job_log_group:highlighted", {
        groupId: this.props.id,
        el: this.wrapperNode,
      });
    }
  }

  componentDidMount() {
    JobLogLineStore.addChangeListener(this.props.id, this.handleStoreChange);
    JobLogGroupStore.addChangeListener(this.props.id, this.handleStoreChange);
    this.dispatchRenderedHighlightAction();
  }

  componentWillUnmount() {
    JobLogLineStore.removeChangeListener(this.props.id, this.handleStoreChange);
    JobLogGroupStore.removeChangeListener(
      this.props.id,
      this.handleStoreChange,
    );
  }

  componentDidUpdate() {
    this.dispatchRenderedHighlightAction();
  }

  handleStoreChange = () => {
    this.setState(getStateFromStores(this.props.id));
  };

  handleToggleHeaderClick = (
    evt: SyntheticMouseEvent<HTMLTableCellElement>,
  ) => {
    evt.preventDefault();

    return LegacyDispatcher.emit("job_log_group:toggle", {
      groupId: this.props.id,
    });
  };

  handleNumberClick = (evt: SyntheticMouseEvent<HTMLTableCellElement>) => {
    evt.preventDefault();

    this.setState({ showCopyPermalink: true });

    return LegacyDispatcher.emit("job_log_line:highlight", {
      lineId: this.props.id,
    });
  };

  handleCopyPermalink = (evt: SyntheticMouseEvent<HTMLButtonElement>) => {
    // Prevent the line from being unhighlighted
    evt.stopPropagation();

    const url = new URL(window.location.href);
    url.hash = this.props.id;
    copy(url.toString());

    this.setState({ showCopyPermalink: false });
  };

  handleTimestampClick = (evt: SyntheticMouseEvent<HTMLElement>) => {
    evt.preventDefault();

    if (this.props.onTimestampClick) {
      this.props.onTimestampClick(evt);
    }
  };

  renderCopyPermalink = () => {
    return (
      <div
        className={classNames("JobLogOutputComponent__CopyPermalink", {
          "JobLogOutputComponent__CopyPermalink--Visible":
            this.state.highlighted && this.state.showCopyPermalink,
        })}
      >
        <CopyPermalink onCopy={this.handleCopyPermalink} />
      </div>
    );
  };

  render() {
    let headerClassName = this.props.bootstrap
      ? "JobLogOutputComponent__Header--Bootstrap"
      : "JobLogOutputComponent__Header";

    if (this.state.highlighted) {
      headerClassName += "--Highlighted";
    }

    const caretClassName = this.state.expanded
      ? "fa-caret-down"
      : "fa-caret-right";

    // Parsing of UTF8 emojis is handled by the backend job log renderer
    const name = parseEmoji(this.props.name, {
      escape: false,
      replaceUnicode: false,
    });

    let timingNode;

    if (this.props.startedAt) {
      // Set the width of the badge to it's percentage of time taken to run
      // the group. We only bother doing this if the percentge is more than
      // 20% (anything lower is kinda insignifigant)
      const badgeStyle =
        this.props.durationPercentage && this.props.durationPercentage > 20
          ? { width: `${this.props.durationPercentage}%` }
          : this.props.startedAt && !this.props.finishedAt
            ? { width: "20px" }
            : {};

      // Only show the timing node if we have the full timing information. We
      // could do live countdowns, but because we only get new data every 5
      // seconds, the counter would go up to 6 seconds, we get new data, then it
      // drops to 2 seconds. So it looks funky town.
      const innerTimingNode = this.props.finishedAt ? (
        <Duration.Short
          from={this.props.startedAt}
          to={this.props.finishedAt}
        />
      ) : this.props.finished ? null : (
        <LegacySpinner />
      );

      timingNode = (
        <span className="JobLogOutputComponent__Time__Badge" style={badgeStyle}>
          {innerTimingNode}
        </span>
      );
    }

    return (
      <tr
        ref={(wrapperNode) => (this.wrapperNode = wrapperNode)}
        className={headerClassName}
      >
        <td
          className="JobLogOutputComponent__Number relative"
          onClick={this.handleNumberClick}
          data-line-number={this.props.number}
        >
          {this.renderCopyPermalink()}
        </td>
        {this.props.timestampsOn && (
          <JobLogOutputTimestamp
            time={this.props.startedAt}
            utc={this.props.timestampsInUTC}
            onClick={this.handleTimestampClick}
          />
        )}
        <td
          className="JobLogOutputComponent__Header__Name"
          onClick={this.handleToggleHeaderClick}
        >
          <span className="JobLogOutputComponent__Caret">
            <i className={`fa ${caretClassName}`} />
          </span>
          <span dangerouslySetInnerHTML={{ __html: name }} />
        </td>
        <td
          className="JobLogOutputComponent__Time"
          onClick={this.handleToggleHeaderClick}
        >
          {timingNode}
        </td>
      </tr>
    );
  }
}
